// src/pages/VolunteerSignupsPage.js
// Updated: 08/25/2025 07:45 PM

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import {
    doc, getDoc,
} from 'firebase/firestore';
import {
    Container, Typography, CircularProgress, Box, List, ListItem,
    ListItemText, Paper, Grid, Divider, Button, Alert, Chip,
    Dialog, DialogActions, DialogContent, DialogTitle, TextField // Import Dialog components
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';
import format from 'date-fns/format';

function VolunteerSignupsPage() {
    const { churchId, opportunityId } = useParams();
    const { userProfile, currentUser } = useAuth();
    const navigate = useNavigate();
    const [opportunity, setOpportunity] = useState(null);
    const [positions, setPositions] = useState([]);
    const [signups, setSignups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isAuthorized, setIsAuthorized] = useState(false);

    // *** FIX: State for the new Log Hours modal ***
    const [isLogHoursModalOpen, setIsLogHoursModalOpen] = useState(false);
    const [currentSignup, setCurrentSignup] = useState(null);
    const [hoursValue, setHoursValue] = useState('');
    const [isSubmittingHours, setIsSubmittingHours] = useState(false);

    useEffect(() => {
        if (!userProfile?.churchId || userProfile.churchId !== churchId || !currentUser?.uid) {
            setError("You do not have permission to view this page.");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                const oppRef = doc(db, 'churches', churchId, 'volunteerOpportunities', opportunityId);
                const oppSnap = await getDoc(oppRef);

                if (!oppSnap.exists()) {
                    navigate('/volunteer');
                    return;
                }
                
                const oppData = { id: oppSnap.id, ...oppSnap.data() };
                setOpportunity(oppData);

                const isAdmin = userProfile?.role === 'Church Administrator' || userProfile?.role === 'Super Administrator';
                const isPoc = oppData.contactPersonId === currentUser.uid;

                if (isAdmin || isPoc) {
                    setIsAuthorized(true);
                    const getSignupsCallable = httpsCallable(functions, 'getVolunteerSignups');
                    const response = await getSignupsCallable({ churchId, opportunityId });
                    const { signups: fetchedSignups, positions: fetchedPositions } = response.data;
                    setSignups(fetchedSignups);
                    setPositions(fetchedPositions);
                } else {
                    setIsAuthorized(false);
                    navigate('/');
                }
            } catch (err) {
                console.error("Error fetching volunteer signup data:", err);
                setError(err.message || "Failed to fetch sign-ups.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();

    }, [churchId, opportunityId, userProfile, currentUser, navigate]);

    const handleCancelSignup = async (userIdToCancel) => {
        if (!window.confirm("Are you sure you want to cancel this person's sign-up?")) return;

        const position = signups.find(s => s.id === userIdToCancel)?.positionId;

        const cancelSignupCallable = httpsCallable(functions, 'cancelVolunteerSignUp');
        try {
            await cancelSignupCallable({ churchId, opportunityId, userIdToCancel, positionId: position });
            const getSignupsCallable = httpsCallable(functions, 'getVolunteerSignups');
            const response = await getSignupsCallable({ churchId, opportunityId });
            setSignups(response.data.signups);
            alert("Sign-up cancelled successfully.");
        } catch (err) {
            console.error("Error cancelling sign-up:", err);
            alert("Failed to cancel sign-up: " + err.message);
        }
    };
    
    // *** FIX: Function to open the modal ***
    const handleOpenLogHoursModal = (signup) => {
        setCurrentSignup(signup);
        setHoursValue(signup.hoursLogged || '');
        setIsLogHoursModalOpen(true);
    };

    const handleCloseLogHoursModal = () => {
        setIsLogHoursModalOpen(false);
        setCurrentSignup(null);
        setHoursValue('');
        setIsSubmittingHours(false);
    };

    // *** FIX: Function to handle the submission from the modal ***
    const handleConfirmLogHours = async () => {
        if (!hoursValue || isNaN(parseFloat(hoursValue)) || parseFloat(hoursValue) < 0) {
            alert("Please enter a valid, non-negative number.");
            return;
        }
        
        setIsSubmittingHours(true);
        const logHoursCallable = httpsCallable(functions, 'logVolunteerHours');
        try {
            await logHoursCallable({
                churchId,
                opportunityId,
                signUpId: currentSignup.id,
                hours: parseFloat(hoursValue),
            });
            const getSignupsCallable = httpsCallable(functions, 'getVolunteerSignups');
            const response = await getSignupsCallable({ churchId, opportunityId });
            setSignups(response.data.signups);
            alert("Hours logged successfully.");
            handleCloseLogHoursModal();
        } catch (err) {
            console.error("Error logging hours:", err);
            alert("Failed to log hours: " + err.message);
            setIsSubmittingHours(false);
        }
    };


    if (loading) {
        return <CircularProgress />;
    }
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!isAuthorized || !opportunity) {
        return (
            <Container>
                <Alert severity="error">You are not authorized to manage this opportunity.</Alert>
            </Container>
        );
    }

    const signupsByPosition = positions.reduce((acc, position) => {
        acc[position.id] = signups.filter(s => s.positionId === position.id);
        return acc;
    }, {});

    const totalSignedUp = signups.length;
    const totalRequired = positions.reduce((sum, p) => sum + p.required, 0);

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom>{opportunity.title}</Typography>
                <Typography variant="h6" color="text.secondary">Sign-ups ({totalSignedUp}/{totalRequired})</Typography>
            </Box>

            <Grid container spacing={3}>
                {positions.map(position => (
                    <Grid item xs={12} md={12} lg={6} key={position.id}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6">{position.name}</Typography>
                                <Chip label={`${signupsByPosition[position.id]?.length || 0}/${position.required}`} color="primary" />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{position.description}</Typography>
                            <Divider sx={{ my: 1 }} />
                            <List dense>
                                {signupsByPosition[position.id]?.length > 0 ? (
                                    signupsByPosition[position.id].map(signup => (
                                        <ListItem 
                                            key={signup.id} 
                                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        >
                                            <ListItemText 
                                                sx={{ flexGrow: 1, mr: 2, overflow: 'hidden' }}
                                                primaryTypographyProps={{ noWrap: true, textOverflow: 'ellipsis' }}
                                                primary={signup.name} 
                                                secondary={format(new Date(signup.signedUpAt), 'PPp')} 
                                            />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                                {/* *** FIX: This button now opens the modal *** */}
                                                <Button size="small" variant="outlined" onClick={() => handleOpenLogHoursModal(signup)}>
                                                    {signup.hoursLogged ? `Logged: ${signup.hoursLogged} hrs` : "Log Hours"}
                                                </Button>
                                                <Button size="small" variant="outlined" color="error" onClick={() => handleCancelSignup(signup.id)}>Cancel</Button>
                                            </Box>
                                        </ListItem>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No sign-ups for this position yet.</Typography>
                                )}
                            </List>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* *** FIX: The new Log Hours Modal *** */}
            <Dialog open={isLogHoursModalOpen} onClose={handleCloseLogHoursModal} maxWidth="xs" fullWidth>
                <DialogTitle>Log Hours for {currentSignup?.name}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="hours"
                        label="Hours Served"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={hoursValue}
                        onChange={(e) => setHoursValue(e.target.value)}
                        inputProps={{ step: "0.1", min: "0" }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseLogHoursModal}>Cancel</Button>
                    <Button onClick={handleConfirmLogHours} variant="contained" disabled={isSubmittingHours}>
                        {isSubmittingHours ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Container>
    );
}

export default VolunteerSignupsPage;